import { useEffect, useMemo, useRef } from 'react';
import { CampingSpot, Coordinates, RouteFollowPoi, FollowMode } from '../types';
import { getDistance, isPointNearPolyline } from '../services/geometryUtils';

export interface FollowTarget {
  id: string;
  name: string;
  location: Coordinates;
  source: 'spot' | 'route_poi';
  spotId: string | null;
  poiCategory?: RouteFollowPoi['category'];
  distanceMeters: number;
}

interface UseFollowTargetArgs {
  mode: FollowMode | null;
  userPosition: Coordinates | null;
  routeCoords?: Coordinates[] | null;
  routeProgressM?: number | null;
  spots: CampingSpot[];
  routePois?: RouteFollowPoi[];
}

const MAX_FREE_DRIVE_DISTANCE_M = 2000;
const MAX_ROUTE_DISTANCE_TO_USER_M = 3000;
const MAX_ROUTE_DISTANCE_TO_POLYLINE_M = 1800;
const SWITCH_MARGIN_M = 120;

export function useFollowTarget({
  mode,
  userPosition,
  routeCoords = null,
  routeProgressM = null,
  spots,
  routePois = [],
}: UseFollowTargetArgs): FollowTarget | null {
  const previousRef = useRef<FollowTarget | null>(null);

  const nextTarget = useMemo<FollowTarget | null>(() => {
    if (!mode || !userPosition) return null;

    if (mode === 'route_follow' && routeCoords && routeCoords.length > 1) {
      const routeSpotCandidates = spots
        .filter((spot) => spot.location && isPointNearPolyline(spot.location, routeCoords, MAX_ROUTE_DISTANCE_TO_POLYLINE_M))
        .map((spot) => {
          const userDistance = getDistance(
            userPosition.latitude,
            userPosition.longitude,
            spot.location.latitude,
            spot.location.longitude
          );
          // Verified spots get a 200m scoring bonus (appear "closer" in ranking)
          const verifiedBonus = spot.isVerified ? -200 : 0;
          return {
            id: `spot-${spot.id}`,
            name: spot.name || 'Punkt',
            location: spot.location,
            source: 'spot' as const,
            spotId: spot.id,
            poiCategory: undefined as RouteFollowPoi['category'] | undefined,
            distanceMeters: userDistance,
            score: userDistance + verifiedBonus,
          };
        })
        .filter((candidate) => candidate.distanceMeters <= MAX_ROUTE_DISTANCE_TO_USER_M);

      const routePoiCandidates = routePois
        .map((poi) => {
          const userDistance = getDistance(
            userPosition.latitude,
            userPosition.longitude,
            poi.location.latitude,
            poi.location.longitude
          );
          const progressDistance = routeProgressM === null ? 0 : Math.abs(poi.distanceAlongRoute - routeProgressM);
          return {
            id: poi.id,
            name: poi.name,
            location: poi.location,
            source: 'route_poi' as const,
            spotId: null,
            poiCategory: poi.category as RouteFollowPoi['category'],
            distanceMeters: userDistance,
            score: userDistance + Math.min(progressDistance, 2500) * 0.12,
          };
        })
        .filter((candidate) => candidate.distanceMeters <= MAX_ROUTE_DISTANCE_TO_USER_M);

      // Prefer Fricamp spots; only fall back to POIs if no spots in range
      const candidates: (FollowTarget & { score: number })[] = routeSpotCandidates.length > 0
        ? routeSpotCandidates as any
        : [...routeSpotCandidates, ...routePoiCandidates] as any;
      const best = candidates.sort((a, b) => a.score - b.score)[0];

      if (!best) return null;

      return {
        id: best.id,
        name: best.name,
        location: best.location,
        source: best.source,
        spotId: best.spotId,
        poiCategory: best.poiCategory,
        distanceMeters: best.distanceMeters,
      };
    }

    const bestSpot = spots
      .map((spot) => ({
        id: `spot-${spot.id}`,
        name: spot.name || 'Punkt',
        location: spot.location,
        source: 'spot' as const,
        spotId: spot.id,
        distanceMeters: getDistance(
          userPosition.latitude,
          userPosition.longitude,
          spot.location.latitude,
          spot.location.longitude
        ),
      }))
      .filter((candidate) => candidate.distanceMeters <= MAX_FREE_DRIVE_DISTANCE_M)
      .sort((a, b) => a.distanceMeters - b.distanceMeters)[0];

    return bestSpot ?? null;
  }, [mode, userPosition, routeCoords, routeProgressM, spots, routePois]);

  useEffect(() => {
    if (!nextTarget) {
      previousRef.current = null;
      return;
    }

    const previous = previousRef.current;
    if (!previous) {
      previousRef.current = nextTarget;
      return;
    }

    if (previous.id === nextTarget.id) {
      previousRef.current = nextTarget;
      return;
    }

    if (previous.distanceMeters <= nextTarget.distanceMeters + SWITCH_MARGIN_M) {
      previousRef.current = previous;
      return;
    }

    previousRef.current = nextTarget;
  }, [nextTarget]);

  return previousRef.current;
}
